import React, { useState, useEffect } from 'react';
import { AccountBalance, AccountType } from '../types/AssetClasses';
import { ASSET_CLASSES } from '../data/AssetClassData';

interface PortfolioInputProps {
  onAccountBalancesChange: (accountBalances: AccountBalance[]) => void;
}

const PortfolioInput: React.FC<PortfolioInputProps> = ({ onAccountBalancesChange }) => {
  const [accountBalances, setAccountBalances] = useState<AccountBalance[]>([]);
  const [allocationMode, setAllocationMode] = useState<'percentage' | 'amount'>('percentage');

  // Initialize account balances with default values
  useEffect(() => {
    if (accountBalances.length === 0) {
      const defaultAccountBalances: AccountBalance[] = [
        {
          accountType: AccountType.TAX_DEFERRED,
          balance: 0,
          allocations: ASSET_CLASSES.map(asset => ({
            assetClassId: asset.id,
            percentage: asset.id === 'USEquity' ? 50 :
                       asset.id === 'InternationalEquity' ? 30 :
                       asset.id === 'AggregateBonds' ? 10 : 10,
            amount: 0
          }))
        },
        {
          accountType: AccountType.TAX_FREE,
          balance: 0,
          allocations: ASSET_CLASSES.map(asset => ({
            assetClassId: asset.id,
            percentage: asset.id === 'USEquity' ? 50 :
                       asset.id === 'InternationalEquity' ? 30 :
                       asset.id === 'AggregateBonds' ? 10 : 10,
            amount: 0
          }))
        },
        {
          accountType: AccountType.TAXABLE_INVESTMENT,
          balance: 0,
          allocations: ASSET_CLASSES.map(asset => ({
            assetClassId: asset.id,
            percentage: asset.id === 'USEquity' ? 50 :
                       asset.id === 'InternationalEquity' ? 30 :
                       asset.id === 'AggregateBonds' ? 10 : 10,
            amount: 0
          }))
        },
        {
          accountType: AccountType.CASH_SAVINGS,
          balance: 0,
          allocations: [{
            assetClassId: 'Cash',
            percentage: 100,
            amount: 0
          }]
        }
      ];
      setAccountBalances(defaultAccountBalances);
    }
  }, [accountBalances.length]);

  useEffect(() => {
    onAccountBalancesChange(accountBalances);
  }, [accountBalances, onAccountBalancesChange]);

  const handleAccountBalanceChange = (accountType: AccountType, value: string) => {
    const numValue = parseFloat(value) || 0;
    
    setAccountBalances(prev => {
      return prev.map(account => 
        account.accountType === accountType 
          ? { 
              ...account, 
              balance: numValue,
              allocations: account.allocations.map(allocation => ({
                ...allocation,
                amount: allocation.percentage / 100 * numValue
              }))
            }
          : account
      );
    });
  };

  const handlePercentageChange = (accountType: AccountType, assetClassId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    
    setAccountBalances(prev => {
      return prev.map(account => 
        account.accountType === accountType 
          ? {
              ...account,
              allocations: account.allocations.map(allocation => 
                allocation.assetClassId === assetClassId 
                  ? { 
                      ...allocation, 
                      percentage: numValue, 
                      amount: (numValue / 100) * account.balance 
                    }
                  : allocation
              )
            }
          : account
      );
    });
  };

  const handleAmountChange = (accountType: AccountType, assetClassId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    
    setAccountBalances(prev => {
      return prev.map(account => 
        account.accountType === accountType 
          ? {
              ...account,
              allocations: account.allocations.map(allocation => 
                allocation.assetClassId === assetClassId 
                  ? { 
                      ...allocation, 
                      amount: numValue, 
                      percentage: account.balance > 0 ? (numValue / account.balance) * 100 : 0 
                    }
                  : allocation
              )
            }
          : account
      );
    });
  };

  const getTotalPercentage = (account: AccountBalance) => {
    return account.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
  };

  const getTotalAmount = (account: AccountBalance) => {
    return account.allocations.reduce((sum, alloc) => sum + alloc.amount, 0);
  };

  const isAllocationValid = (account: AccountBalance) => {
    if (allocationMode === 'percentage') {
      return Math.abs(getTotalPercentage(account) - 100) < 0.01;
    } else {
      return Math.abs(getTotalAmount(account) - account.balance) < 0.01;
    }
  };

  const getTotalPortfolioValue = () => {
    return accountBalances.reduce((sum, account) => sum + account.balance, 0);
  };

  const getAccountTypeName = (accountType: AccountType) => {
    switch (accountType) {
      case AccountType.TAX_DEFERRED:
        return 'Tax Deferred (401k, etc.)';
      case AccountType.TAX_FREE:
        return 'Tax Free (Roth, HSA)';
      case AccountType.TAXABLE_INVESTMENT:
        return 'Taxable Investment';
      case AccountType.CASH_SAVINGS:
        return 'Cash Savings';
      default:
        return accountType;
    }
  };

  return (
    <div className="portfolio-input">
      <h2>Starting Assets</h2>
      
      <div className="total-portfolio-summary">
        <h3>Total Liquid Assets: ${getTotalPortfolioValue().toLocaleString()}</h3>
      </div>

      {/* Cash Savings - separate section */}
      {(() => {
        const cashAccount = accountBalances.find(account => account.accountType === AccountType.CASH_SAVINGS);
        if (!cashAccount) return null;
        
        return (
          <div className="portfolio-input-section">
            <h3>{getAccountTypeName(AccountType.CASH_SAVINGS)}</h3>
            <div className="cash-savings-content">
              <div className="cash-balance">
                <label htmlFor={`balance-${AccountType.CASH_SAVINGS}`}>Balance ($):</label>
                <input
                  id={`balance-${AccountType.CASH_SAVINGS}`}
                  type="number"
                  value={cashAccount.balance}
                  onChange={(e) => handleAccountBalanceChange(AccountType.CASH_SAVINGS, e.target.value)}
                  placeholder="Enter cash savings balance"
                  min="0"
                  step="1000"
                />
              </div>
            </div>
          </div>
        );
      })()}
      <div className="portfolio-input-section">
          <h3>Investment Accounts</h3>
          <div className="allocation-mode">
            <label>
              <input
                type="radio"
                value="percentage"
                checked={allocationMode === 'percentage'}
                onChange={(e) => setAllocationMode(e.target.value as 'percentage' | 'amount')}
              />
              Allocate by Percentage
            </label>
            <label>
              <input
                type="radio"
                value="amount"
                checked={allocationMode === 'amount'}
                onChange={(e) => setAllocationMode(e.target.value as 'percentage' | 'amount')}
              />
              Allocate by Amount
            </label>
          </div>
          <div className="portfolio-grid">
          {/* Header row */}
          <div className="grid-header">
            <div className="balance-header">Balance ($)</div>
            {accountBalances
              .filter(account => account.accountType !== AccountType.CASH_SAVINGS)
              .map(account => (
                <div key={account.accountType} className="account-header">
                  <div className="account-name">
                    {getAccountTypeName(account.accountType)}
                  </div>
                  <div className="account-balance">
                    <input
                      id={`balance-${account.accountType}`}
                      type="number"
                      value={account.balance}
                      onChange={(e) => handleAccountBalanceChange(account.accountType, e.target.value)}
                      placeholder="Balance"
                      min="0"
                      step="1000"
                    />
                  </div>
                </div>
              ))}
          </div>

          {/* Asset class rows */}
          {ASSET_CLASSES.map(asset => (
            <div key={asset.id} className="asset-row">
              <div className="asset-name">
                <label>{asset.name}</label>
              </div>
              
              {accountBalances
                .filter(account => account.accountType !== AccountType.CASH_SAVINGS)
                .map(account => {
                  const allocation = account.allocations.find(a => a.assetClassId === asset.id);
                  if (!allocation) return null;
                  
                  return (
                    <div key={`${account.accountType}-${asset.id}`} className="allocation-cell">
                      <div className="allocation-input-container">
                        {allocationMode === 'percentage' ? (
                          <input
                            id={`${account.accountType}-${allocation.assetClassId}`}
                            type="number"
                            value={allocation.percentage || 0}
                            onChange={(e) => handlePercentageChange(account.accountType, allocation.assetClassId, e.target.value)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.1"
                          />
                        ) : (
                          <input
                            id={`${account.accountType}-${allocation.assetClassId}`}
                            type="number"
                            value={allocation.amount || 0}
                            onChange={(e) => handleAmountChange(account.accountType, allocation.assetClassId, e.target.value)}
                            placeholder="0"
                            min="0"
                            step="1000"
                          />
                        )}
                        <div className="allocation-display">
                          {allocationMode === 'percentage' ? (
                            <span className="amount-display">
                              ${((allocation.percentage || 0) / 100 * account.balance).toLocaleString()}
                            </span>
                          ) : (
                            <span className="percentage-display">
                              {account.balance > 0 ? (((allocation.amount || 0) / account.balance) * 100).toFixed(1) : 0}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

            </div>
          ))}

          {/* Account totals row */}
          <div className="totals-row">
            <div className="totals-label"></div>
            {accountBalances
              .filter(account => account.accountType !== AccountType.CASH_SAVINGS)
              .map(account => (
                <div key={`total-${account.accountType}`} className="account-total-cell">
                  <div className={`account-total-display ${isAllocationValid(account) ? 'valid' : 'invalid'}`}>
                    {allocationMode === 'percentage' ? (
                      <div>
                        <div className="account-total-percentage">
                          {getTotalPercentage(account).toFixed(2)}%
                        </div>
                        {!isAllocationValid(account) && <div className="error">Must equal 100%</div>}
                      </div>
                    ) : (
                      <div>
                        <div className="account-total-amount">
                          ${getTotalAmount(account).toLocaleString()}
                        </div>
                        {!isAllocationValid(account) && <div className="error">Must equal balance</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioInput;
